import { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Select } from '../components_catalyst/select';
import { Label, Fieldset } from '../components_catalyst/fieldset';
import { RootState } from '../store/store';
import type { AppDispatch } from '../store/store';
import { fetchMilestoneMap, fetchObjectives } from '../store/map3DataSlice';

interface DependencyFormProps {
  onSave: (dependency: {
    sourceResultUuid: string;
    sourceObjectiveUuid: string;
    targetResultUuid: string;
    targetObjectiveUuid: string;
    targetMilestoneMapUuid: string;
  }) => void;
  onValidate?: (dependency: {
    sourceResultUuid: string;
    sourceObjectiveUuid: string;
    targetResultUuid: string;
    targetObjectiveUuid: string;
    targetMilestoneMapUuid: string;
  }) => void;
  onCancel?: () => void;
  initialValues?: {
    sourceResultUuid: string;
    sourceObjectiveUuid: string;
    targetResultUuid: string;
    targetObjectiveUuid: string;
    targetMilestoneMapUuid: string;
  };
  context: 'objective' | 'map';
  currentMilestoneMapUuid: string;
  currentObjectiveUuid?: string;
  validationError?: string;
}

/**
 * Form for adding/editing dependencies between results
 *
 * In objective context (maps/:mapId/objectives/:objectiveId/dependencies):
 * - Source result must be from current objective
 * - Source objective is locked to current objective
 * - Target result can be from any objective in the map
 * - Target objective can be any objective in the map (except parents)
 *
 * In map context (maps/:mapId/dependencies):
 * - Source/target results can be from any objective
 * - Source/target objectives can be any objective (except parents)
 */
export function DependencyForm({
  onSave,
  onValidate,
  onCancel,
  initialValues,
  context,
  currentMilestoneMapUuid,
  currentObjectiveUuid = '',
  validationError,
}: DependencyFormProps) {
  // Form state
  const [sourceResultUuid, setSourceResultUuid] = useState(initialValues?.sourceResultUuid ?? '');
  const [sourceObjectiveUuid, setSourceObjectiveUuid] = useState(
    context === 'objective' ? currentObjectiveUuid : (initialValues?.sourceObjectiveUuid ?? ''),
  );
  const [targetResultUuid, setTargetResultUuid] = useState(initialValues?.targetResultUuid ?? '');
  const [targetObjectiveUuid, setTargetObjectiveUuid] = useState(
    initialValues?.targetObjectiveUuid ?? '',
  );
  const [selectedMapUuid, setSelectedMapUuid] = useState(
    initialValues?.targetMilestoneMapUuid ?? currentMilestoneMapUuid,
  );

  // Add state for source and target map selection
  const [sourceMapUuid, setSourceMapUuid] = useState(currentMilestoneMapUuid);
  const [targetMapUuid, setTargetMapUuid] = useState(
    initialValues?.targetMilestoneMapUuid ?? currentMilestoneMapUuid,
  );

  // Get user profile and all available maps
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);
  const milestoneMaps = [
    ...(userProfile.myMilestoneMaps || []),
    ...(userProfile.sharedMilestoneMaps || []),
  ];
  // Get the full map object for the selected map
  const fullMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.uuid === selectedMapUuid),
  );

  // Get the full map object for the selected source and target maps
  const fullSourceMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.uuid === sourceMapUuid),
  );
  const fullTargetMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.uuid === targetMapUuid),
  );

  // Get all objectives for the selected map
  const allObjectives = useSelector((state: RootState) => {
    if (!fullMap) return [];
    return state.map3Data.objectives.filter((obj) => fullMap.objectiveUuids.includes(obj.uuid));
  });

  // Get all objectives for the selected source and target maps
  const allSourceObjectives = useSelector((state: RootState) => {
    if (!fullSourceMap) return [];
    return state.map3Data.objectives.filter((obj) =>
      fullSourceMap.objectiveUuids.includes(obj.uuid),
    );
  });
  const allTargetObjectives = useSelector((state: RootState) => {
    if (!fullTargetMap) return [];
    return state.map3Data.objectives.filter((obj) =>
      fullTargetMap.objectiveUuids.includes(obj.uuid),
    );
  });

  // Get current objective (if in objective context)
  const currentObjective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.uuid === currentObjectiveUuid),
  );

  // Get results for source objective
  const sourceObjective = useMemo(() => {
    if (!sourceObjectiveUuid) return null;
    return allSourceObjectives.find((obj) => obj.uuid === sourceObjectiveUuid);
  }, [sourceObjectiveUuid, allSourceObjectives]);

  const sourceResults = sourceObjective?.results || [];

  // Get results for target objective
  const targetObjective = useMemo(() => {
    if (!targetObjectiveUuid) return null;
    return allTargetObjectives.find((obj) => obj.uuid === targetObjectiveUuid);
  }, [targetObjectiveUuid, allTargetObjectives]);

  const targetResults = targetObjective?.results || [];

  // Filter available target objectives (exclude current objective and map's parent objectives)
  const availableTargetObjectives = useMemo(() => {
    if (!fullTargetMap) return [];
    return allTargetObjectives.filter((obj) => {
      const isCurrentObjective = obj.uuid === currentObjectiveUuid;
      const isParentObjective = fullTargetMap.parentObjectiveUuids?.includes(obj.uuid);
      return !isCurrentObjective && !isParentObjective;
    });
  }, [currentObjectiveUuid, fullTargetMap, allTargetObjectives]);

  // Handlers for map changes
  const handleSourceMapChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSourceMapUuid(e.target.value);
    setSourceObjectiveUuid('');
    setSourceResultUuid('');
  };
  const handleTargetMapChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTargetMapUuid(e.target.value);
    setTargetObjectiveUuid('');
    setTargetResultUuid('');
  };

  // Handlers for objective changes (ensure result resets)
  const handleSourceObjectiveChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSourceObjectiveUuid(e.target.value);
    setSourceResultUuid('');
  };
  const handleTargetObjectiveChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTargetObjectiveUuid(e.target.value);
    setTargetResultUuid('');
    if (e.target.value) {
      onValidate?.({
        sourceResultUuid,
        sourceObjectiveUuid: context === 'objective' ? currentObjectiveUuid : sourceObjectiveUuid,
        targetResultUuid: '',
        targetObjectiveUuid: e.target.value,
        targetMilestoneMapUuid: targetMapUuid,
      });
    }
  };

  const handleSourceResultChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSourceResultUuid(e.target.value);
    onValidate?.({
      sourceResultUuid: e.target.value,
      sourceObjectiveUuid: context === 'objective' ? currentObjectiveUuid : sourceObjectiveUuid,
      targetResultUuid,
      targetObjectiveUuid,
      targetMilestoneMapUuid: selectedMapUuid,
    });
  };

  const handleTargetResultChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTargetResultUuid(e.target.value);
    if (e.target.value) {
      onValidate?.({
        sourceResultUuid,
        sourceObjectiveUuid: context === 'objective' ? currentObjectiveUuid : sourceObjectiveUuid,
        targetResultUuid: e.target.value,
        targetObjectiveUuid,
        targetMilestoneMapUuid: targetMapUuid,
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted', { sourceResultUuid, targetObjectiveUuid, targetResultUuid });
    onSave({
      sourceResultUuid,
      sourceObjectiveUuid: context === 'objective' ? currentObjectiveUuid : sourceObjectiveUuid,
      targetResultUuid,
      targetObjectiveUuid,
      targetMilestoneMapUuid: targetMapUuid,
    });
  };

  const dispatch: AppDispatch = useDispatch();

  // Fetch source map and objectives when sourceMapUuid changes
  useEffect(() => {
    if (sourceMapUuid) {
      (async () => {
        try {
          const map = await dispatch(fetchMilestoneMap(sourceMapUuid)).unwrap();
          if (map && map.objectiveUuids) {
            await dispatch(fetchObjectives(map.objectiveUuids));
          }
        } catch (e) {}
      })();
    }
  }, [sourceMapUuid, dispatch]);

  // Fetch target map and objectives when targetMapUuid changes
  useEffect(() => {
    if (targetMapUuid) {
      (async () => {
        try {
          const map = await dispatch(fetchMilestoneMap(targetMapUuid)).unwrap();
          if (map && map.objectiveUuids) {
            await dispatch(fetchObjectives(map.objectiveUuids));
          }
        } catch (e) {}
      })();
    }
  }, [targetMapUuid, dispatch]);

  // Debug log for form state
  console.log({
    sourceResultUuid,
    targetObjectiveUuid,
    targetResultUuid,
    validationError,
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {/* Source Fields */}
      <Fieldset>
        <Label>Select result</Label>
        <Select value={sourceResultUuid} onChange={handleSourceResultChange}>
          <option value="">Choose a result</option>
          {sourceResults.map((result) => (
            <option key={result.uuid} value={result.uuid}>
              {result.text}
            </option>
          ))}
        </Select>
      </Fieldset>
      <Fieldset>
        <Label>from</Label>
        <Select
          value={sourceObjectiveUuid}
          onChange={handleSourceObjectiveChange}
          disabled={context === 'objective'}
        >
          <option value="">Choose an objective</option>
          {allSourceObjectives.map((objective) => (
            <option key={objective.uuid} value={objective.uuid}>
              {objective.text}
            </option>
          ))}
        </Select>
      </Fieldset>
      <Fieldset>
        <Label htmlFor="source-map-select">on</Label>
        <Select
          id="source-map-select"
          aria-label="Select source milestone map"
          value={sourceMapUuid}
          onChange={handleSourceMapChange}
          disabled={context === 'objective' || context === 'map'}
        >
          {context === 'objective' && fullSourceMap ? (
            <option value={fullSourceMap.uuid}>{fullSourceMap.title}</option>
          ) : (
            <>
              <option value="">Choose a map</option>
              {milestoneMaps.map((map) => (
                <option key={map.uuid} value={map.uuid}>
                  {map.title}
                </option>
              ))}
            </>
          )}
        </Select>
      </Fieldset>

      {/* Static "depends on" text */}
      <div className="py-2 text-sm text-gray-600 text-center">depends on</div>

      {/* Target Fields */}
      <Fieldset>
        <Label>Select result</Label>
        <Select
          value={targetResultUuid}
          onChange={handleTargetResultChange}
          disabled={!targetObjectiveUuid}
        >
          <option value="">Choose a result</option>
          {targetResults.map((result) => (
            <option key={result.uuid} value={result.uuid}>
              {result.text}
            </option>
          ))}
        </Select>
      </Fieldset>
      <Fieldset>
        <Label>from</Label>
        <Select value={targetObjectiveUuid} onChange={handleTargetObjectiveChange}>
          <option value="">Choose an objective</option>
          {availableTargetObjectives.map((objective) => (
            <option key={objective.uuid} value={objective.uuid}>
              {objective.text}
            </option>
          ))}
        </Select>
      </Fieldset>
      <Fieldset>
        <Label htmlFor="target-map-select">on</Label>
        <Select
          id="target-map-select"
          aria-label="Select target milestone map"
          value={targetMapUuid}
          onChange={handleTargetMapChange}
          disabled={false}
        >
          <option value="">Choose a map</option>
          {milestoneMaps.map((map) => (
            <option key={map.uuid} value={map.uuid}>
              {map.title}
            </option>
          ))}
        </Select>
      </Fieldset>

      {/* Validation Error Message */}
      {validationError && <div className="text-sm text-red-600 mt-2">{validationError}</div>}

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          onClick={() => console.log('Button clicked')}
          className="px-3 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
          disabled={
            !sourceResultUuid ||
            !targetObjectiveUuid ||
            !targetResultUuid ||
            Boolean(validationError)
          }
        >
          {initialValues ? 'Save Changes' : 'Add Dependency'}
        </button>
      </div>
    </form>
  );
}
